<template>
  <Form
    id="content-page-form"
    :form-id="FinancialFormIDs.MEAL_PLAN_FORM"
    :schema="schema"
    :initialValues="initialValues"
    :keepValuesOnUnmount="true"
    @onValidateSuccess="handleValidationSuccess"
    @onValidateFail="handleValidationFail"
    :loading="saving"
    ref="formComponent"
  >
    <template v-slot="{ isFormValid, form }">
      <MealPlanGrid
        :disablePlanName="!editMealPlan"
        :disablePlanType="!editMealPlan"
        :emptyStateString="t('sites.singleSite.financialConfig.mealPlans.empty')"
        :showMEQOrMX="false"
      >
        <template #atrium>
          <Col6><Key /></Col6>
          <Col6><APIUrl /></Col6>
          <Col6><Note /></Col6>
          <Col6><AtriumTerminalID /></Col6>
          <Col12><Domains /></Col12>
        </template>

        <template #atriumSSO>
          <Col6><Key /></Col6>
          <Col6><APIUrl /></Col6>
          <Col6><WebviewURL /></Col6>
          <Col6><Client /></Col6>
          <Col6><Note /></Col6>
          <Col6><AtriumTerminalID /></Col6>
          <Col12><Domains /></Col12>
        </template>

        <template #cbordDirect>
          <Col12><CBORDUUID /></Col12>
          <Col6><Note /></Col6>
          <Col6><CbordTerminalID /></Col6>
        </template>

        <template #cbordWeb>
          <Col6><CBORDUUID /></Col6>
          <Col6><CBORDAuthURL /></Col6>
          <Col6><Note /></Col6>
          <Col6><CbordTerminalID /></Col6>
        </template>
        <template #itc>
          <Col6><PlanId /></Col6>
          <Col6><Certificate /></Col6>
          <Col6><CertificatePassword /></Col6>
          <Col6><ConnectionURL /></Col6>
          <Col6><VendorId /></Col6>
          <Col6><WebviewURL /></Col6>
          <Col12><TerminalIDNumberField /></Col12>
        </template>
        <template #transact>
          <Col6><TransactHost /></Col6>
          <Col6><TransactPort /></Col6>
          <Col6><TransactAESKey /></Col6>
          <Col6><TransactVendorNumber /></Col6>
          <Col6><Note /></Col6>
          <Col6><TransactTerminalID /></Col6>
          <Col6><TransactSSO /></Col6>
          <Col6><Domains /></Col6>
        </template>

        <template #touchnet>
          <Col6><OperatorID /></Col6>
          <Col6><OperatorPassword /></Col6>
          <Col6><TouchnetAPI /></Col6>
          <Col6><TaxExempt /></Col6>
          <Col12><PlanId /></Col12>
          <Col6><Note /></Col6>
          <Col6><TouchnetTerminalID /></Col6>
        </template>
      </MealPlanGrid>
    </template>
  </Form>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { Form } from '@centric-os/components/src/composed/forms/form';
import useGlobalToast from '@centric-os/components/src/core/messages/toast/composables/use-global-toast';
import { Col6 } from '@centric-os/components/src/core/layouts/columns/col-6';
import { Col12 } from '@centric-os/components/src/core/layouts/columns/col-12';
import { useMealPlan } from './composables';
import MealPlanGrid from './meal-plan-grid/MealPlanGrid.vue';
import { onUnmounted, ref } from 'vue';
import { useEventBus } from '@vueuse/core';
import type { ApiResponseError, SitePrivateConfig, SitePublicConfig } from '@centric-os/types';
import { useSiteStore } from '@centric-os/stores/sites/use-site-store';
import { useStoreAPIAction } from '@centric-os/stores/plugins/api';
import { storeToRefs } from 'pinia';
import { useFinancialConfigStore, useSiteConfigStore } from '../../../../routes';
import { useMealPlanSchema } from '../../../../composables';
import { FinancialFormIDs } from '../enums';
import CbordTerminalID from './fields/cbord-terminal-id/CBORDTerminalID.vue';
import CBORDUUID from './fields/cbord-uuid/CBORDUUID.vue';
import CBORDAuthURL from './fields/cbord-auth-url/CBORDAuthURL.vue';
import WebviewURL from './fields/webview-url/WebviewURL.vue';
import Client from './fields/client/Client.vue';
import AtriumTerminalID from './fields/atrium-terminal-id/AtriumTerminalID.vue';
import { useMealPlanStore } from '../../../../routes/stores/use-meal-plan-store';
import PlanId from './fields/plan-id/PlanId.vue';
import Certificate from './fields/certificate/Certificate.vue';
import CertificatePassword from './fields/certificate-password/CertificatePassword.vue';
import ConnectionURL from './fields/connection-url/ConnectionURL.vue';
import VendorId from './fields/vendor-id/VendorId.vue';
import TerminalIDNumberField from './fields/terminal-id-number-field/TerminalIDNumberField.vue';
import Key from './fields/key/Key.vue';
import APIUrl from './fields/api-url/APIUrl.vue';
import Note from './fields/note/Note.vue';
import Domains from './domains/Domains.vue';
import TransactHost from './fields/transact-host/TransactHost.vue';
import TransactPort from './fields/transact-port/TransactPort.vue';
import TransactAESKey from './fields/transact-aes-key/TransactAESKey.vue';
import TransactVendorNumber from './fields/transact-vendor-number/TransactVendorNumber.vue';
import TransactTerminalID from './fields/transact-terminal-id/TransactTerminalID.vue';
import OperatorID from './fields/operator-id/OperatorID.vue';
import OperatorPassword from './fields/operator-password/OperatorPassword.vue';
import TouchnetAPI from './fields/touchnet-api/TouchnetAPI.vue';
import TaxExempt from './fields/tax-exempt/TaxExempt.vue';
import TouchnetTerminalID from './fields/touchnet-terminal-id/TouchnetTerminalID.vue';
import TransactSSO from './fields/transact-sso/TransactSSO.vue';

const { t } = useI18n();

const mealPlanStore = useMealPlanStore();
const { loading: saving } = useStoreAPIAction(mealPlanStore, 'saveMealPlan');

const fConfigStore = useFinancialConfigStore();
const { editMealPlan } = storeToRefs(fConfigStore);

const { schema } = useMealPlanSchema();

const formComponent = ref<InstanceType<typeof Form>>();

const { showErrorToast, showSuccessToast } = useGlobalToast();

const { initialValues, mapSitePrivateConfigPayload, mapSitePublicConfigPayload } = useMealPlan();

const save = async (): Promise<void> => {
  formComponent.value?.validate();
};

// Handle validation success
const { on: onMealPlan, off: offMealPlan } = useEventBus<void>(`save-meal-plan-site`);
// Set the listener for validate and submit form
onMealPlan(save);

const handleValidationSuccess = async (form: any): Promise<void> => {
  let privatePayload: SitePrivateConfig = null;
  let publicPayload: SitePublicConfig = null;

  const siteStore = useSiteStore();
  const { site } = storeToRefs(siteStore);
  const id = site.value.id as string;

  privatePayload = mapSitePrivateConfigPayload(form);
  publicPayload = mapSitePublicConfigPayload(form);

  await mealPlanStore
    .saveMealPlan(id, true, privatePayload, publicPayload)
    .then(async () => {
      const siteConfigStore = useSiteConfigStore();
      await siteConfigStore.fetchSiteConfigurations(id);

      showSuccessToast(t('sites.singleSite.financialConfig.mealPlans.form.success'));
      editMealPlan.value = false;
    })
    .catch((error: ApiResponseError) => {
      if (error.status) {
        if (error.status >= 400 && error.status < 500) {
          showErrorToast(error?.error ? error?.error : (error?.message as string));
        } else {
          showErrorToast(t('errorMessages.genericApiError'));
        }
      }
    });
};

const handleValidationFail = (): void => {
  showErrorToast(t('sites.singleSite.financialConfig.mealPlans.form.validationFailed'));
};

onUnmounted(() => {
  offMealPlan(save);
});
</script>
